/**
 * Auto-coordination: the rudder a pilot's feet would be doing.
 *
 * ## Why this exists at all
 *
 * A single-engine propeller aeroplane is not in trim about the yaw axis except at one power
 * setting and one speed. Change either and it needs rudder — that is real, it is modelled, and
 * it is why the fin is rigged (see `sparrow.json`) rather than why the physics should be softened.
 * But a pilot has two feet and a keyboard player has two more keys competing with roll, pitch,
 * throttle and everything else, and holding a few degrees of rudder for a whole flight with the
 * `E` key is not the skill the simulator is trying to teach. So the assist supplies the standing
 * correction and leaves the interesting rudder — the slip, the crosswind landing, the spin
 * recovery — to the pilot.
 *
 * ## Two mechanisms, because there are two regimes
 *
 * The assist shipped as a pure **sideslip-nulling feedback loop**, inhibited on the ground and
 * below 18 m/s. That is the right controller in the air and it is *useless* in the regime where
 * the aeroplane is hardest to keep straight — the full-power take-off roll — for two reasons that
 * are both about β rather than about gains:
 *
 *   - **On the ground there is no β to null yet.** The wheels hold the nose pointing where it is
 *     pointing, so a yaw moment shows up first as a heading change and a departure from the
 *     centreline, and only later as sideslip. By the time β is measurable the aeroplane is
 *     already off the line.
 *   - **At 10 kt there is nothing to null it *with*.** Feedback needs authority proportional to
 *     its error, and free-stream dynamic pressure at walking pace is nil.
 *
 * What is available from the instant the throttle moves is the *moment itself*: the propulsion
 * model computes P-factor and slipstream swirl every tick, and the airframe's own fin geometry
 * says what a unit of rudder is worth at the current propwash. So the ground and low-speed
 * regime is served by **feed-forward** — divide the disturbance by the authority and push that
 * much pedal — which is what a pilot's feet actually do: they anticipate from the power setting
 * rather than waiting to feel a slip.
 *
 * The two run together and hand over smoothly. Feed-forward is full strength on the ground and
 * fades out as the aeroplane accelerates past flying speed, where β becomes both meaningful and
 * correctable; the β loop fades in over the same band. Yaw-rate damping runs throughout, and it
 * is what mops up whatever the feed-forward mis-estimates — notably nosewheel steering, which
 * takes the *same* rudder command and adds yaw authority the aerodynamic estimate knows nothing
 * about.
 *
 * ## What it deliberately does not do
 *
 *   - **It yields instantly to the pilot.** Any rudder input fades the assist out proportionally,
 *     so a deliberate slip is a slip and not an argument with the computer. The fade is on the
 *     *input*, not on the output, so the moment the key is released the assist is back — and it
 *     applies to both mechanisms, so a crosswind take-off is still flown with the feet.
 *   - **It has limited authority.** It can supply the standing correction and no more. On the
 *     ground that ceiling is higher than in the air, because on the ground the correction it is
 *     standing in for is most of the rudder a pilot uses; it still cannot reach the stops.
 *   - **It does not steer.** There is no heading hold and no centreline capture. It cancels a
 *     known moment; if the aeroplane is pointing somewhere else, that is the pilot's to fix.
 *     An assist that quietly flew the take-off would be a different feature and a worse one.
 *   - **It does not touch the trim.** Rudder trim is the pilot's, and the two must not chase each
 *     other; the assist reads only what the airframe is doing.
 *
 * `updateFixed(dt)`: yes — it runs on the physics tick, because it is a control loop and its
 * gains are in per-second units. Allocation-free.
 */

import { DEG2RAD, clamp } from '../core/math.js';

/** What the assist needs to know about the airframe this tick. */
export interface CoordinationFeedback {
  /** Sideslip [rad], positive when the nose is left of the relative wind. */
  betaRad: number;
  /** Yaw rate in body axes [rad/s], positive nose-right. */
  yawRateRadPerS: number;
  /** True airspeed [m/s]. */
  trueAirspeedMps: number;
  /** True while any wheel is on the ground. */
  onGround: boolean;
  /** Roll rate in body axes [rad/s], positive right-wing-down. Drives the lateral auto-trim. */
  rollRateRadPerS?: number;
  /** Bank angle [rad], positive right-wing-down. Lets the auto-trim pick a dropped wing back up. */
  bankRad?: number;
  /**
   * The propeller's standing yaw moment [N·m], positive nose-right — `FlightDynamicsModel.
   * yawBalance.disturbanceNm`. Zero, or left undefined, disables the feed-forward term.
   */
  yawDisturbanceNm?: number;
  /**
   * Yaw moment per unit of rudder command [N·m] — `yawBalance.rudderAuthorityNm`. Positive
   * means a positive command yaws the nose right.
   */
  rudderAuthorityNm?: number;
}

export interface CoordinationOptions {
  /** Rudder command per radian of sideslip. */
  proportionalGain?: number;
  /** Rudder command per rad/s of yaw rate — damping, so it settles instead of hunting. */
  dampingGain?: number;
  /** Rudder command per radian of heading departure from the datum, on the ground [1/rad]. */
  headingHoldGain?: number;
  /**
   * Rudder per radian-second of heading departure, on the ground [1/(rad·s)].
   *
   * The trim-up term, and it is not optional. Feed-forward inverts a linearisation and will always
   * be a few percent out; damping cannot remove what is left, because a constant residual moment
   * simply settles at a constant yaw rate; and the heading-hold term alone leaves a steady offset
   * proportional to that residual — measured, about 1°, which is 11 m of centreline over a 600 m
   * roll. Integrating the departure is what drives it to zero, and it is exactly what a pilot does:
   * add foot until the nose stops moving, then add a little more until it comes back.
   */
  integralGain?: number;
  /** Ceiling on the integrator's own contribution, 0…1, so it can never wind up to the stops. */
  maxIntegral?: number;
  /** Time constant on the ground/air handover for the heading hold [s]. */
  groundHoldFadeS?: number;
  /** Hard ceiling on the assist's own authority in the air, 0…1. */
  maxAuthority?: number;
  /**
   * Ceiling on the ground and at low speed, 0…1.
   *
   * Higher than {@link maxAuthority} because down there the assist is standing in for most of
   * what a pilot's feet are doing rather than for a standing trim offset — a trainer at full
   * power below rotation speed genuinely wants a large fraction of the available rudder. Still
   * short of the stops, so a real departure is still the pilot's problem.
   */
  groundAuthority?: number;
  /** Pilot rudder beyond which the assist is fully out of the way. */
  pilotOverrideAt?: number;
  /** Airspeed below which the sideslip loop is inhibited [m/s] — β is noise down there. */
  minAirspeedMps?: number;
  /** Airspeed at which the feed-forward has faded out entirely [m/s]. */
  feedForwardFadeMps?: number;
  /** How much of the computed disturbance the feed-forward tries to cancel, 0…1. */
  feedForwardShare?: number;
  /** Smoothing half-life on the assist's output [s], so it never adds a step input. */
  smoothingHalfLifeS?: number;
  /**
   * Aileron trim wound in per rad/s of roll rate, per second [1/rad].
   *
   * The lateral half of the assist. Torque reaction is a standing left-roll moment at every power
   * setting, and it is *small* — measured, 0.031 of aileron holds it in the climb. That is the
   * problem: the smallest input a keyboard can make is a 0.1 s tap, which is 0.066, and a held key
   * reaches full deflection in half a second. A pilot on a keyboard therefore cannot hold 0.031 of
   * aileron; they can only oscillate around it, which is exactly what "very hard to fly" feels
   * like. Winding it into the *trim* puts the stick back at zero and makes every input the pilot
   * does make a manoeuvre rather than a correction.
   */
  lateralTrimGain?: number;
  /** Ceiling on the auto-trim, as a fraction of the aircraft's aileron trim range. */
  maxLateralTrim?: number;
  /** Trim wound in per radian of bank, per second [1/(rad·s)] — picks a dropped wing back up. */
  lateralLevelGain?: number;
  /** Bank below which the wing-picking-up term is at full strength [rad]. */
  levelFullBelowRad?: number;
  /** Bank beyond which it is off entirely [rad] — past here, you are turning on purpose. */
  levelOffAboveRad?: number;
  /** Pilot roll input beyond which the auto-trim stops accumulating. */
  rollFreezeAt?: number;
}

/**
 * Default gains.
 *
 * The proportional gain is not a guess: at the sparrow's cruise, before the fin was rigged, an
 * uncorrected 2.7° of sideslip needed 0.20 of rudder, which is 4.2 per radian. Four is that,
 * rounded down so the loop is comfortably stable, and the damping term is sized to take the
 * dutch-roll oscillation out rather than to contribute authority of its own.
 *
 * `feedForwardShare` is below 1 deliberately. The feed-forward inverts a *linearisation* of the
 * fin — see `FlightDynamicsModel.updateYawBalance` — and the rudder command it produces also
 * drives the nosewheel, which adds yaw authority the estimate does not model. Asking it to cancel
 * the whole disturbance therefore over-corrects on the ground, and an assist that swings the nose
 * right is worse than one that leaves a little for the damping term to absorb. Under-cancelling
 * fails in the direction the pilot expects.
 */
const DEFAULTS: Required<CoordinationOptions> = {
  proportionalGain: 4,
  dampingGain: 0.55,
  headingHoldGain: 5,
  integralGain: 4,
  maxIntegral: 0.35,
  groundHoldFadeS: 0.6,
  maxAuthority: 0.55,
  groundAuthority: 0.9,
  pilotOverrideAt: 0.12,
  minAirspeedMps: 18,
  feedForwardFadeMps: 32,
  feedForwardShare: 0.85,
  smoothingHalfLifeS: 0.18,
  lateralTrimGain: 1.6,
  lateralLevelGain: 0.9,
  // 8° and 15°. A wing that has dropped 5° is drift; a 30° bank is a turn, and an assist that
  // levelled it would be flying the aeroplane. Measured with no gate at all, holding a 30° turn
  // hands-off wound 0.42 of trim in against it — over half the range, and all of it left there
  // to fight the roll-out.
  levelFullBelowRad: 8 * DEG2RAD,
  levelOffAboveRad: 15 * DEG2RAD,
  maxLateralTrim: 0.75,
  rollFreezeAt: 0.04,
};

export class CoordinationAssist {
  private readonly options: Required<CoordinationOptions>;
  private enabledValue = true;
  private outputValue = 0;
  private feedForwardValue = 0;
  /** Aileron trim the assist has wound in, −1…1. See {@link CoordinationOptions.lateralTrimGain}. */
  private lateralTrimValue = 0;
  /** 1 while the wheels are down, easing to 0 after liftoff. See {@link groundHold}. */
  private groundHoldValue = 0;
  /** Accumulated heading change since the datum was captured [rad]. */
  private headingDriftRad = 0;
  /** Integral of {@link headingDriftRad}, as rudder. See {@link CoordinationOptions.integralGain}. */
  private integralValue = 0;

  constructor(options: CoordinationOptions = {}) {
    this.options = { ...DEFAULTS, ...options };
  }

  get enabled(): boolean {
    return this.enabledValue;
  }

  setEnabled(enabled: boolean): void {
    this.enabledValue = enabled;
    if (!enabled) {
      this.outputValue = 0;
      this.feedForwardValue = 0;
      this.integralValue = 0;
      this.headingDriftRad = 0;
      this.groundHoldValue = 0;
      this.lateralTrimValue = 0;
    }
  }

  toggle(): boolean {
    this.setEnabled(!this.enabledValue);
    return this.enabledValue;
  }

  /** The assist's current contribution to the rudder command, −1…1. For the HUD. */
  get output(): number {
    return this.outputValue;
  }

  /** How much of {@link output} is feed-forward rather than feedback, −1…1. Diagnostics only. */
  get feedForward(): number {
    return this.feedForwardValue;
  }

  /** The trim-up term's current value, −1…1. Diagnostics only. */
  get integral(): number {
    return this.integralValue;
  }

  /** Heading departure from the ground datum [rad]. Diagnostics only. */
  get headingDrift(): number {
    return this.headingDriftRad;
  }

  /** How much of the ground heading hold is engaged, 0…1. Diagnostics only. */
  get groundHold(): number {
    return this.groundHoldValue;
  }

  /**
   * The aileron trim the assist is holding, −1…1.
   *
   * Fold this into the pilot's own aileron trim — it is a *trim*, not a control, so the stick
   * stays where the pilot put it and only its zero point moves.
   */
  get lateralTrim(): number {
    return this.lateralTrimValue;
  }

  /**
   * Wind the lateral trim toward whatever holds the wings.
   *
   * Called on the physics tick alongside {@link updateFixed}. Separate from it because it is a
   * different axis with a different contract: this one never touches the *control*, only the trim.
   *
   * @param pilotRoll  the pilot's own aileron command, −1…1.
   * @returns the trim setting, to be added to the pilot's manual aileron trim.
   */
  updateLateralTrim(pilotRoll: number, feedback: CoordinationFeedback, dtSeconds: number): number {
    const {
      lateralTrimGain,
      lateralLevelGain,
      levelFullBelowRad,
      levelOffAboveRad,
      maxLateralTrim,
      rollFreezeAt,
      minAirspeedMps,
    } = this.options;
    const rollRate = feedback.rollRateRadPerS;

    /*
     * Frozen unless the aeroplane is flying and the pilot has let go.
     *
     * While they are rolling, the roll rate is *theirs* and integrating it would wind the trim up
     * against them — so a deliberate turn must freeze it, not fight it. On the ground the wheels
     * decide the bank angle and there is nothing aerodynamic to trim.
     */
    if (
      !this.enabledValue ||
      rollRate === undefined ||
      feedback.onGround ||
      feedback.trueAirspeedMps < minAirspeedMps ||
      Math.abs(pilotRoll) > rollFreezeAt
    ) {
      return this.lateralTrimValue;
    }

    /*
     * The wing-picking-up term, gated on how far over the aeroplane is.
     *
     * Inside `levelFullBelowRad` a bank is drift and a pilot would trim it out; beyond
     * `levelOffAboveRad` it is a turn they are flying, and trimming against it would be an
     * autopilot wrestling the pilot. The roll-*rate* term needs no such gate — in a steady turn
     * the roll rate is zero, so it simply stops.
     */
    const bankRad = feedback.bankRad ?? 0;
    const levelAuthority = clamp(
      (levelOffAboveRad - Math.abs(bankRad)) / Math.max(1e-6, levelOffAboveRad - levelFullBelowRad),
      0,
      1,
    );
    this.lateralTrimValue = clamp(
      this.lateralTrimValue -
        (lateralTrimGain * rollRate + lateralLevelGain * bankRad * levelAuthority) * dtSeconds,
      -maxLateralTrim,
      maxLateralTrim,
    );
    return this.lateralTrimValue;
  }

  /**
   * Fold the assist into a pilot rudder command.
   *
   * @param pilotYaw  What the pilot is asking for, −1…1.
   * @returns the combined rudder command, −1…1.
   */
  updateFixed(pilotYaw: number, feedback: CoordinationFeedback, dtSeconds: number): number {
    const {
      proportionalGain,
      dampingGain,
      maxAuthority,
      groundAuthority,
      headingHoldGain,
      integralGain,
      maxIntegral,
      groundHoldFadeS,
      pilotOverrideAt,
      minAirspeedMps,
      feedForwardFadeMps,
      feedForwardShare,
    } = this.options;

    let target = 0;
    let feedForward = 0;

    if (this.enabledValue) {
      const speed = feedback.trueAirspeedMps;

      /*
       * How much of each mechanism this condition wants.
       *
       * One crossfade drives both, so they always sum to a sensible whole and neither can be on
       * twice. Feed-forward owns the ground roll and everything below `minAirspeedMps`, hands
       * over across the band up to `feedForwardFadeMps` — which is a shade above rotation speed,
       * so the transition happens once the wing is flying and β means something — and the
       * sideslip loop owns everything above it. On the ground the feed-forward is at full weight
       * whatever the speed, because a landing roll at 50 kt has exactly the same problem as a
       * take-off roll at 5: the wheels, not the fin, are deciding where the nose points.
       */
      const airborneBlend = feedback.onGround
        ? 0
        : clamp((speed - minAirspeedMps) / Math.max(1e-6, feedForwardFadeMps - minAirspeedMps), 0, 1);

      // --- feed-forward: cancel the moment the propeller is making ---------------------------
      const disturbance = feedback.yawDisturbanceNm ?? 0;
      const authority = feedback.rudderAuthorityNm ?? 0;
      if (Math.abs(authority) > 1 && airborneBlend < 1) {
        // Divide the moment by what a unit of rudder is worth, and push back. The sign takes
        // care of itself: a nose-left disturbance on a positive-authority rudder gives positive
        // (right) pedal.
        const cancelling = (-disturbance / authority) * feedForwardShare;
        feedForward = cancelling * (1 - airborneBlend);
      }

      // --- feedback: null the sideslip, damp the rate ------------------------------------------
      // β is positive when the nose is left of the airflow, and positive rudder swings the nose
      // right — so the correction has the same sign as β with no negation to get wrong.
      const slipTerm = proportionalGain * feedback.betaRad * airborneBlend;
      /*
       * Damping runs in *both* regimes, unlike the slip term.
       *
       * It is what catches everything the feed-forward gets wrong — the linearised fin, and above
       * all the nosewheel, which takes the same rudder command and contributes yaw authority the
       * aerodynamic estimate cannot see. Yaw rate is measured directly by the rigid body and is
       * just as valid at 5 kt as at 100, which β is not.
       */
      const dampingTerm = -dampingGain * feedback.yawRateRadPerS;

      // Hand back authority in proportion to what the pilot is asking for, so a deliberate slip
      // is never a tug of war. At full override the assist contributes exactly nothing.
      const pilotAuthority =
        1 - Math.min(1, Math.abs(pilotYaw) / Math.max(1e-6, pilotOverrideAt));
      const ceiling = maxAuthority + (groundAuthority - maxAuthority) * (1 - airborneBlend);

      /*
       * Heading hold, on the ground and at low speed only.
       *
       * The datum is not a compass bearing — it is *wherever the nose was* when the regime became
       * active, tracked by integrating yaw rate. That is deliberate: the assist has no idea what
       * the runway heading is and should not need one, and a departure accumulated from the
       * aeroplane's own gyro is exactly the error a pilot is correcting when they hold the far end
       * of the runway in the windscreen.
       *
       * `pilotAuthority` decays the datum rather than gating it. While the pilot is steering, the
       * aeroplane is going where *they* are pointing it, so the hold re-datums to the new heading
       * as they go and stops fighting to return to the old one the moment they let go — which is
       * the difference between an assist and an argument. At full override the datum tracks the
       * nose exactly and both terms are zero.
       */
      /*
       * Heading hold follows the *wheels*, not the airspeed.
       *
       * It was first gated on the same speed crossfade as everything else, and that left it half
       * active in the initial climb — where holding a heading is emphatically not the assist's
       * job, and where it fought the climbing turn. What makes heading hold correct is that the
       * gear is deciding where the nose points; the moment the wheels leave, that stops being
       * true. The fade is only so liftoff is not a step input.
       */
      const groundTarget = feedback.onGround ? 1 : 0;
      this.groundHoldValue +=
        (groundTarget - this.groundHoldValue) *
        (groundHoldFadeS > 0 ? Math.min(1, dtSeconds / groundHoldFadeS) : 1);
      const groundWeight = this.groundHoldValue;

      let groundTerm = 0;
      if (groundWeight > 0.001) {
        this.headingDriftRad += feedback.yawRateRadPerS * dtSeconds * groundWeight;
        // Re-datum toward the current heading in proportion to how much pedal the pilot has in.
        this.headingDriftRad -= this.headingDriftRad * Math.min(1, (1 - pilotAuthority) * dtSeconds * 8);
        this.integralValue = clamp(
          this.integralValue - integralGain * this.headingDriftRad * dtSeconds * pilotAuthority,
          -maxIntegral,
          maxIntegral,
        );
        groundTerm = (-headingHoldGain * this.headingDriftRad + this.integralValue) * groundWeight;
      }
      if (groundWeight < 1) {
        // Airborne, the sideslip loop owns this axis; a held-over ground integral arriving in the
        // climb would be a standing rudder offset nobody asked for.
        const bleed = Math.min(1, dtSeconds * 1.6 * (1 - groundWeight));
        this.integralValue -= this.integralValue * bleed;
        this.headingDriftRad -= this.headingDriftRad * bleed;
      }

      feedForward *= pilotAuthority;
      target = clamp(
        feedForward + (slipTerm + dampingTerm + groundTerm) * pilotAuthority,
        -ceiling,
        ceiling,
      );
    }

    // Ease toward the target rather than jumping to it: a rudder that steps is a rudder you can
    // feel, and the point of the assist is that you cannot.
    const k =
      this.options.smoothingHalfLifeS > 0
        ? 1 - Math.pow(2, -dtSeconds / this.options.smoothingHalfLifeS)
        : 1;
    this.outputValue += (target - this.outputValue) * k;
    this.feedForwardValue = feedForward;

    return clamp(pilotYaw + this.outputValue, -1, 1);
  }

  /** Forget the accumulated output, e.g. on respawn. */
  reset(): void {
    this.outputValue = 0;
    this.feedForwardValue = 0;
    this.integralValue = 0;
    this.headingDriftRad = 0;
    this.groundHoldValue = 0;
    this.lateralTrimValue = 0;
  }
}
